import type { ResolvedConfig } from "../types";

/** What to do about a response, or about the absence of one. */
export type SendVerdict = "ok" | "retry" | "drop";

/**
 * `null` means the request never produced a response — offline, DNS failure, CORS. Those
 * are worth retrying. So are 408, 429 and anything 5xx.
 *
 * Every other 4xx is a contract problem: the same payload will be rejected identically
 * every time, so retrying only burns a bounded queue. Drop it instead.
 */
export function classify(status: number | null): SendVerdict {
	if (status === null) return "retry";
	if (status >= 200 && status < 300) return "ok";
	if (status === 408 || status === 429 || status >= 500) return "retry";
	return "drop";
}

/**
 * Exponential backoff with equal jitter — half the delay fixed, half random — so a wave of
 * tabs coming back online does not retry in lockstep.
 */
export function backoffDelay(attempt: number, config: ResolvedConfig): number {
	const exponential = Math.min(config.maxRetryDelayMs, config.retryBaseDelayMs * 2 ** attempt);
	return exponential / 2 + Math.random() * (exponential / 2);
}

export function sleep(ms: number): Promise<void> {
	return new Promise(resolve => setTimeout(resolve, ms));
}
